import { useState } from 'react'
import type { ApiWebsiteDoctorResult } from '../utils/api'

// ── §63: Website Doctor — комплексный аудит сайта ────────────────────────────

const DIM_ICONS: Record<string, string> = {
  'SEO': '🔍',
  'Performance': '⚡',
  'Accessibility': '♿',
  'Security': '🔐',
  'Content Quality': '📝',
  'UX / Design': '🎨',
  'Conversion': '💰',
  'Technical': '⚙️',
  'Mobile': '📱',
  'Analytics': '📊',
}

const SEV_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high:     '#f59e0b',
  medium:   '#3b82f6',
  low:      '#6b7280',
}

function scoreColor(v: number) {
  return v >= 80 ? '#22c55e' : v >= 60 ? '#f59e0b' : '#ef4444'
}

function DimensionCard({ dim }: { dim: ApiWebsiteDoctorResult['dimensions'][0] }) {
  const [open, setOpen] = useState(false)
  const c = scoreColor(dim.score)
  const icon = Object.entries(DIM_ICONS).find(([k]) => dim.name.includes(k))?.[1] ?? '●'
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: '#fff', border: 'none', cursor: 'pointer',
          textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#111' }}>{dim.name}</span>
        <div style={{ width: 80, height: 5, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ height: '100%', width: `${dim.score}%`, background: c, borderRadius: 3 }} />
        </div>
        <span style={{ width: 28, textAlign: 'right', fontSize: 13, fontWeight: 700, color: c, flexShrink: 0 }}>{dim.score}</span>
        {dim.issues.length > 0 && (
          <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
        )}
      </button>
      {open && dim.issues.length > 0 && (
        <div style={{ padding: '4px 14px 12px', background: '#fafafa', borderTop: '1px solid #f3f4f6' }}>
          {dim.issues.map((issue, i) => (
            <div key={i} style={{ padding: '8px 10px', background: '#fff', border: `1px solid ${SEV_COLOR[issue.severity] ?? '#e5e7eb'}33`, borderRadius: 8, marginBottom: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: SEV_COLOR[issue.severity] ?? '#6b7280',
                  textTransform: 'uppercase', background: (SEV_COLOR[issue.severity] ?? '#6b7280') + '18',
                  padding: '1px 5px', borderRadius: 4,
                }}>{issue.severity}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{issue.title}</span>
              </div>
              <div style={{ fontSize: 11, color: '#4b5563', marginBottom: 3 }}>{issue.description}</div>
              <div style={{ fontSize: 11, color: '#5b6af0', display: 'flex', gap: 3 }}>
                <span style={{ fontWeight: 600 }}>→</span>
                <span>{issue.fix}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface Props {
  result: ApiWebsiteDoctorResult
  onClose: () => void
}

export function WebsiteDoctorModal({ result, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<'dimensions' | 'priorities'>('dimensions')
  const hc = scoreColor(result.healthScore)

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
            border: `4px solid ${hc}`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexDirection: 'column', background: `${hc}12`,
          }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: hc, lineHeight: 1 }}>{result.healthScore}</span>
            <span style={{ fontSize: 9, color: '#9ca3af', lineHeight: 1, marginTop: 2 }}>/100</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 2 }}>
              🩺 Website Doctor — Оценка {result.grade}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 8 }}>{result.summary}</div>
            <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
              {result.criticalCount > 0 && <span style={{ color: '#ef4444', fontWeight: 700 }}>🔴 {result.criticalCount} критично</span>}
              {result.highCount > 0    && <span style={{ color: '#f59e0b', fontWeight: 700 }}>🟡 {result.highCount} высоких</span>}
              {result.mediumCount > 0  && <span style={{ color: '#3b82f6', fontWeight: 700 }}>🔵 {result.mediumCount} средних</span>}
              {result.lowCount > 0     && <span style={{ color: '#6b7280', fontWeight: 600 }}>⚪ {result.lowCount} низких</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af', flexShrink: 0, alignSelf: 'flex-start' }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ padding: '10px 24px 0', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 4, flexShrink: 0 }}>
          {(['dimensions', 'priorities'] as const).map(t => (
            <button key={t} onClick={() => setActiveTab(t)} style={{
              padding: '7px 16px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
              fontSize: 12, fontWeight: activeTab === t ? 700 : 400,
              background: activeTab === t ? '#111' : 'transparent',
              color: activeTab === t ? '#fff' : '#6b7280',
              borderBottom: activeTab === t ? '2px solid #111' : '2px solid transparent',
              marginBottom: '-1px',
            }}>
              {t === 'dimensions' ? `Разделы (${result.dimensions.length})` : `Приоритеты (${result.topPriorities.length})`}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {activeTab === 'dimensions' && (
            <div>
              {result.dimensions.map((dim, i) => (
                <DimensionCard key={i} dim={dim} />
              ))}
            </div>
          )}
          {activeTab === 'priorities' && (
            <div>
              {result.topPriorities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#22c55e', fontSize: 13 }}>
                  ✓ Сайт в отличном состоянии
                </div>
              ) : (
                result.topPriorities.map((p, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                    padding: '12px 14px', background: i % 2 === 0 ? '#f9fafb' : '#fff',
                    border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 8,
                  }}>
                    <span style={{
                      width: 24, height: 24, borderRadius: '50%', background: '#5b6af0',
                      color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>{i + 1}</span>
                    <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{p}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
